import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Calendar, Clock, Music, Utensils, ChevronDown, ChevronUp } from 'lucide-react';

const EVENTS = [
  {
    id: 'nye-2026',
    image: 'https://customer-assets-lxgj4vgw.emergentagent.net/job_6c1e1185-4e2b-4a47-b7ad-68a7aff64ff4/artifacts/d89940c5f5c25b0e_Gouden%20oudejaarsavond%20bij%20Barbaar.png',
    menuImage: 'https://customer-assets-lxgj4vgw.emergentagent.net/job_6c1e1185-4e2b-4a47-b7ad-68a7aff64ff4/artifacts/72aeb9df1968988b_Gouden%20BarBaaR%20Oudejaarsavondmenu.png',
    titleNl: "New Year's Eve at BARBAAR",
    titleEn: "New Year's Eve at BARBAAR",
    taglineNl: 'Sip. Share. Sparkle. Enjoy... A Night to Remember.',
    taglineEn: 'Sip. Share. Sparkle. Enjoy... A Night to Remember.',
    dateNl: 'Woensdag 31 december \u2022 19:00 - 02:00',
    dateEn: 'Wednesday 31 December \u2022 19:00 - 02:00',
    startNl: '\u20ac175 per persoon \u2022 All-in',
    startEn: '\u20ac175 per person \u2022 All-in',
    highlights: [
      { icon: 'utensils', nl: 'Exclusive Sharing Menu', en: 'Exclusive Sharing Menu' },
      { icon: 'music', nl: 'Music & Great Vibes', en: 'Music & Great Vibes' },
    ],
    bodyNl: `Laat het oude jaar achter je en beleef een onvergetelijke avond vol verfijnde smaken, sprankelende bubbels en geweldige vibes. Geniet van een culinair feest om samen te delen, in de unieke BarBaaR sfeer.

\u2501\u2501\u2501 WELCOME @ BARBAAR \u2501\u2501\u2501
Champagne Haton met bijhorende warme en koude bites

Ierse Oisri Oester
Hoorntje Charcoal \u2013 Rilette van zalm \u2013 Wasabi \u2013 Zeekraal
Pastaparels \u2013 Grijze garnaal \u2013 Tartaar van tomaat \u2013 Bloody Mary
Tarte Tatin van appel \u2013 Foie gras van eendenlever
Soesje met Escargot de Bourgogne
Kroketje van fazant met Vadouvanmayo

\u2501\u2501\u2501 SEAFOOD TO SHARE \u2501\u2501\u2501
Bon Vivant Chardonnay \u2013 Frankrijk

Bisque d\u2019Homard \u2013 Dumpling van kreeft \u2013 Tomaat \u2013 Venkel
Thai Glazed Gamba\u2019s
Wangetje van zeewolf \u2013 Velout\u00e9 van kokos en limoen

Vergezeld van breekbrood met trio van kruidenboters

\u2501\u2501\u2501 WILD THINGS TO SHARE \u2501\u2501\u2501
Vinha do Monte \u2013 Portugal

Kroketje van wild everzwijn en rode wijn \u2013 Veenbessenmayo
Stoofpotje van everzwijn \u2013 Kastanje
Gekonfijte schenkel van everzwijn \u2013 Wildsaus met donkere chocolade \u2013 Veenbessen \u2013 Borretane uitjes

Vergezeld van:
Madeliene van aardappel
Krokante salade van witloof met Granny Smith
Pannetje met wilde champignons en tuinbonen

\u2501\u2501\u2501 MIDNIGHT KISS \u2501\u2501\u2501
Estrella Moscatel de Valencia

Cr\u00e8me van Siciliaanse citroen \u2013 Champagnesorbet \u2013 Framboos \u2013 Meringue

\u2501\u2501\u2501 CAFE\u00cfNE CLOSING \u2501\u2501\u2501
Koffie met kleine zoetigheden.

\u2501\u2501\u2501 COUNTDOWN TO 2027 \u2501\u2501\u2501
Glaasje champagne Haton om te klinken op het nieuwe jaar.

\u2501\u2501\u2501 DRANKEN INBEGREPEN TOT 02:00 \u2501\u2501\u2501
Witte wijn Bon Vivant \u2013 Rode wijn Vinha do Monte \u2013 Waters \u2013 Cola \u2013 Cola Zero
Cristal \u2013 Blonde & Donkere Affligem \u2013 Blonde & Donkere Grimbergen \u2013 Duvel \u2013 Hapkin
Cristal 0.0 \u2013 Affligem Blond 0.0 \u2013 Affligem Donker 0.0 \u2013 Ricard

Dresscode: A Touch of Gold
\u20ac175 per persoon \u2022 ALL-IN`,
    bodyEn: `Leave the old year behind and experience an unforgettable evening full of refined flavours, sparkling bubbles and great vibes. Enjoy a culinary feast to share together, in the unique BarBaaR atmosphere.

\u2501\u2501\u2501 WELCOME @ BARBAAR \u2501\u2501\u2501
Champagne Haton with accompanying warm and cold bites

Irish Oisri Oyster
Charcoal Cone \u2013 Salmon Rilette \u2013 Wasabi \u2013 Samphire
Pasta Pearls \u2013 Grey Shrimp \u2013 Tomato Tartare \u2013 Bloody Mary
Apple Tarte Tatin \u2013 Duck Liver Foie Gras
Choux with Escargot de Bourgogne
Pheasant Croquette with Vadouvan Mayo

\u2501\u2501\u2501 SEAFOOD TO SHARE \u2501\u2501\u2501
Bon Vivant Chardonnay \u2013 France

Bisque d\u2019Homard \u2013 Lobster Dumpling \u2013 Tomato \u2013 Fennel
Thai Glazed Prawns
Wolffish Cheek \u2013 Coconut & Lime Velout\u00e9

Served with bread and a trio of herb butters

\u2501\u2501\u2501 WILD THINGS TO SHARE \u2501\u2501\u2501
Vinha do Monte \u2013 Portugal

Wild Boar & Red Wine Croquette \u2013 Cranberry Mayo
Wild Boar Stew \u2013 Chestnut
Confit Wild Boar Shank \u2013 Game Sauce with Dark Chocolate \u2013 Cranberries \u2013 Borretane Onions

Accompanied by:
Potato Madeleine
Crispy Endive Salad with Granny Smith
Pan of Wild Mushrooms and Runner Beans

\u2501\u2501\u2501 MIDNIGHT KISS \u2501\u2501\u2501
Estrella Moscatel de Valencia

Sicilian Lemon Cream \u2013 Champagne Sorbet \u2013 Raspberry \u2013 Meringue

\u2501\u2501\u2501 CAFFEINE CLOSING \u2501\u2501\u2501
Coffee with petit fours.

\u2501\u2501\u2501 COUNTDOWN TO 2027 \u2501\u2501\u2501
Glass of Champagne Haton to toast to the new year.

\u2501\u2501\u2501 DRINKS INCLUDED UNTIL 02:00 \u2501\u2501\u2501
White wine Bon Vivant \u2013 Red wine Vinha do Monte \u2013 Waters \u2013 Cola \u2013 Cola Zero
Cristal \u2013 Blonde & Dark Affligem \u2013 Blonde & Dark Grimbergen \u2013 Duvel \u2013 Hapkin
Cristal 0.0 \u2013 Affligem Blond 0.0 \u2013 Affligem Dark 0.0 \u2013 Ricard

Dresscode: A Touch of Gold
\u20ac175 per person \u2022 ALL-IN`,
  },
  {
    id: 'dinner-vibes',
    image: 'https://customer-assets-lxgj4vgw.emergentagent.net/job_6c1e1185-4e2b-4a47-b7ad-68a7aff64ff4/artifacts/cbiphvd1_ChatGPT%20Image%205%20sep%202026%2C%2014_22_38.png',
    titleNl: 'BarBaaR Dinner & Vibes',
    titleEn: 'BarBaaR Dinner & Vibes',
    taglineNl: 'Come for dinner. Stay for the vibe.',
    taglineEn: 'Come for dinner. Stay for the vibe.',
    dateNl: 'Elke vrijdag & zaterdag \u2022 Vanaf 18:00',
    dateEn: 'Every Friday & Saturday \u2022 From 18:00',
    startNl: 'Starting 18/09',
    startEn: 'Starting 18/09',
    highlights: [
      { icon: 'utensils', nl: 'Dinner & sharing from 18:00', en: 'Dinner & sharing from 18:00' },
      { icon: 'music', nl: 'Music & good vibes till 01:00', en: 'Music & good vibes till 01:00' },
    ],
    bodyNl: `Start your evening with good food, great drinks & even better company.

Kom langs voor onze sharing dishes, cocktails, bubbels, bieren en alles wat een geslaagde avond nodig heeft. Neem je tijd, deel, proef en geniet\u2026

En wanneer de borden langzaam van tafel verdwijnen, draaien we de muziek n\u00e9t dat tikkeltje harder. \ud83c\udfb6

De lichten wat zachter.
De beats wat beter\u2026
De cocktails blijven komen.

En de sfeer? Die bouwen we samen op.

Geen klassieke party night.
Gewoon BarBaaR zoals je ons kent\u2026 maar dan met een beetje extra vibe, mood & ambiance.

Apero turns into Dinner.
Dinner turns into drinks.
Drinks turn into music.

And suddenly\u2026 you\u2019re staying a little longer. \ud83c\udfb6

Eat \u2022 Sip \u2022 Share \u2022 Vibe

Come for dinner. Stay for the vibe.
(or just come for some good drinks and the vibe)

And yes... Dansen is toegestaan`,
    bodyEn: `Start your evening with good food, great drinks & even better company.

Come for our sharing dishes, cocktails, bubbles, beers and everything a great night needs. Take your time, share, taste and enjoy\u2026

And when the plates slowly disappear from the table, we turn the music up just a notch. \ud83c\udfb6

The lights a little softer.
The beats a little better\u2026
The cocktails keep coming.

And the vibe? We build it together.

Not a classic party night.
Just BarBaaR as you know us\u2026 but with a little extra vibe, mood & ambiance.

Apero turns into Dinner.
Dinner turns into drinks.
Drinks turn into music.

And suddenly\u2026 you\u2019re staying a little longer. \ud83c\udfb6

Eat \u2022 Sip \u2022 Share \u2022 Vibe

Come for dinner. Stay for the vibe.
(or just come for some good drinks and the vibe)

And yes... Dancing is allowed`,
  },
];

function EventCard({ event, language }) {
  const [expanded, setExpanded] = useState(false);
  const title = language === 'nl' ? event.titleNl : event.titleEn;
  const tagline = language === 'nl' ? event.taglineNl : event.taglineEn;
  const date = language === 'nl' ? event.dateNl : event.dateEn;
  const start = language === 'nl' ? event.startNl : event.startEn;
  const body = language === 'nl' ? event.bodyNl : event.bodyEn;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card overflow-hidden"
      data-testid={`event-card-${event.id}`}
    >
      {/* Header - always visible, clickable */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left"
        data-testid={`event-toggle-${event.id}`}
      >
        <div className="flex flex-col md:flex-row">
          <div className="md:w-1/3 lg:w-2/5 flex-shrink-0">
            <img
              src={event.image}
              alt={title}
              className="w-full h-64 md:h-80 object-cover"
            />
          </div>
          <div className="flex-1 p-6 md:p-8 flex flex-col justify-center">
            <span className="inline-block text-[#C8A46A] font-body text-xs tracking-[0.2em] uppercase mb-2">{start}</span>
            <h3 className="font-heading text-2xl md:text-3xl text-[#EDEDED] mb-2">{title}</h3>
            <p className="font-body text-[#C8A46A] italic text-lg mb-4">{tagline}</p>
            <div className="flex items-center gap-2 text-[#A1A1A1] font-body text-sm mb-4">
              <Calendar size={16} className="text-[#C8A46A]" />
              <span>{date}</span>
            </div>
            <div className="flex flex-wrap gap-4 mb-4">
              {event.highlights.map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-[#A1A1A1] font-body text-sm">
                  {h.icon === 'utensils' && <Utensils size={14} className="text-[#C8A46A]" />}
                  {h.icon === 'music' && <Music size={14} className="text-[#C8A46A]" />}
                  {h.icon === 'clock' && <Clock size={14} className="text-[#C8A46A]" />}
                  <span>{language === 'nl' ? h.nl : h.en}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 text-[#C8A46A] font-body text-sm tracking-wider uppercase">
              <span>{expanded ? (language === 'nl' ? 'Minder info' : 'Less info') : (language === 'nl' ? 'Meer info' : 'More info')}</span>
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>
        </div>
      </button>

      {/* Expandable body */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
            data-testid={`event-body-${event.id}`}
          >
            <div className="px-6 md:px-8 pb-8 pt-2 border-t border-[#2A2A2A]">
              <div className="max-w-3xl mx-auto">
                {event.menuImage && (
                  <div className="mb-8">
                    <img
                      src={event.menuImage}
                      alt={`${title} menu`}
                      className="w-full max-w-md mx-auto rounded-sm shadow-lg"
                    />
                  </div>
                )}
                {body.split('\n').map((line, i) => (
                  <p key={i} className={`font-body text-[#A1A1A1] leading-relaxed ${line.trim() === '' ? 'h-4' : line.includes('\u2501') ? 'text-[#C8A46A] font-semibold text-center text-sm tracking-[0.15em] uppercase mt-6 mb-2' : 'mb-1'}`}>
                    {line.includes('\u2501') ? line.replace(/\u2501/g, '').trim() : line}
                  </p>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function Events() {
  const { language, t } = useLanguage();

  return (
    <main className="min-h-screen bg-[#0A0A0A] pt-32 pb-20" data-testid="events-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block text-[#C8A46A] font-body text-sm tracking-[0.3em] uppercase mb-4"
          >
            {t.eventsPage?.subtitle || 'Wat er gebeurt'}
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#EDEDED] mb-6"
          >
            Events
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-body text-[#A1A1A1] max-w-2xl mx-auto text-base"
          >
            {t.eventsPage?.description || 'Ontdek onze evenementen en speciale avonden bij BARBAAR.'}
          </motion.p>
        </div>

        {/* Event cards */}
        <div className="space-y-8">
          {EVENTS.map((event) => (
            <EventCard key={event.id} event={event} language={language} />
          ))}
        </div>
      </div>
    </main>
  );
}
